/**
 * Paragraph abstraction over CanvasKit Paragraph
 *
 * Provides a more natural interface for working with paragraphs while
 * maintaining compatibility with CanvasKit's underlying implementation.
 * Includes caching for expensive operations like getParagraphInfo().
 */

import { deleteParagraph, trackParagraph } from '@ck';
import type {
  Paragraph as CKParagraph,
  GlyphInfo,
  LineMetrics,
  ParagraphInfo,
  PositionWithAffinity,
  RectHeightStyle,
  RectWidthStyle,
  RectWithDirection,
  URange
} from 'canvaskit';
import type { MeasuredText } from './ckTextUtils';

/**
 * Our abstraction over CanvasKit's Paragraph
 *
 * Provides convenient methods and caching while maintaining
 * access to the underlying CanvasKit paragraph for rendering operations.
 */
export class Paragraph {
  readonly #ckParagraph: CKParagraph;
  readonly #measuredText: MeasuredText;
  #paragraphInfo?: ParagraphInfo; // Lazily cached

  constructor(ckParagraph: CKParagraph, measuredText: MeasuredText) {
    this.#ckParagraph = trackParagraph(ckParagraph);
    this.#measuredText = measuredText;
  }

  /**
   * Get the text content of this paragraph
   */
  get text(): string {
    return this.#measuredText.text;
  }

  /**
   * Get the measured text data for this paragraph
   */
  get measuredText(): MeasuredText {
    return this.#measuredText;
  }

  /**
   * Get cached paragraph info, computing it if needed
   */
  private getParagraphInfo(): ParagraphInfo {
    if (!this.#paragraphInfo) {
      this.#paragraphInfo = this.#ckParagraph.getParagraphInfo(this.#measuredText.text);
    }
    return this.#paragraphInfo;
  }

  /**
   * Get the shaped lines from the cached paragraph info
   */
  get lines() {
    return this.getParagraphInfo().lines;
  }

  /**
   * Get the line metrics from the cached paragraph info
   */
  get lineMetrics() {
    return this.getParagraphInfo().lineMetrics;
  }

  /**
   * Get the enhanced glyph runs from the cached paragraph info
   */
  get runs() {
    return this.getParagraphInfo().runs;
  }

  /**
   * Delegate all other methods to ckParagraph
   * These maintain the same API as CanvasKit's Paragraph
   */
  didExceedMaxLines(): boolean {
    return this.#ckParagraph.didExceedMaxLines();
  }

  getAlphabeticBaseline(): number {
    return this.#ckParagraph.getAlphabeticBaseline();
  }

  getGlyphPositionAtCoordinate(dx: number, dy: number): PositionWithAffinity {
    return this.#ckParagraph.getGlyphPositionAtCoordinate(dx, dy);
  }

  getClosestGlyphInfoAtCoordinate(dx: number, dy: number): GlyphInfo | null {
    return this.#ckParagraph.getClosestGlyphInfoAtCoordinate(dx, dy);
  }

  getGlyphInfoAt(index: number): GlyphInfo | null {
    return this.#ckParagraph.getGlyphInfoAt(index);
  }

  getHeight(): number {
    return this.#ckParagraph.getHeight();
  }

  getIdeographicBaseline(): number {
    return this.#ckParagraph.getIdeographicBaseline();
  }

  getLineNumberAt(index: number): number {
    return this.#ckParagraph.getLineNumberAt(index);
  }

  getLineMetrics(): LineMetrics[] {
    return this.#ckParagraph.getLineMetrics();
  }

  getLineMetricsAt(lineNumber: number): LineMetrics | null {
    return this.#ckParagraph.getLineMetricsAt(lineNumber);
  }

  getLongestLine(): number {
    return this.#ckParagraph.getLongestLine();
  }

  getMaxIntrinsicWidth(): number {
    return this.#ckParagraph.getMaxIntrinsicWidth();
  }

  getMaxWidth(): number {
    return this.#ckParagraph.getMaxWidth();
  }

  getMinIntrinsicWidth(): number {
    return this.#ckParagraph.getMinIntrinsicWidth();
  }

  getNumberOfLines(): number {
    return this.#ckParagraph.getNumberOfLines();
  }

  getRectsForPlaceholders(): RectWithDirection[] {
    return this.#ckParagraph.getRectsForPlaceholders();
  }

  getRectsForRange(
    start: number,
    end: number,
    hStyle: RectHeightStyle,
    wStyle: RectWidthStyle
  ): RectWithDirection[] {
    return this.#ckParagraph.getRectsForRange(start, end, hStyle, wStyle);
  }

  getWordBoundary(offset: number): URange {
    return this.#ckParagraph.getWordBoundary(offset);
  }

  getShapedLines() {
    return this.#ckParagraph.getShapedLines();
  }

  layout(width: number): void {
    this.#ckParagraph.layout(width);
    // Clear cached info when layout changes as it may be invalidated
    this.#paragraphInfo = undefined;
  }

  unresolvedCodepoints(): number[] {
    return this.#ckParagraph.unresolvedCodepoints();
  }

  /**
   * Access the underlying CanvasKit Paragraph for direct operations
   * Use this when you need to pass the paragraph to CanvasKit drawing functions
   */
  get ckParagraph(): CKParagraph {
    return this.#ckParagraph;
  }

  /**
   * Get a string representation for debugging
   */
  toString(): string {
    return `Paragraph(height=${this.getHeight()}, lines=${this.getNumberOfLines()})`;
  }

  /**
   * Clean up the underlying CanvasKit paragraph
   * Must be called when done with the paragraph to prevent memory leaks
   */
  delete(): void {
    deleteParagraph(this.#ckParagraph);
  }
}
